<script setup lang="ts">
import type { RegionCode } from '#shared/lib/region'
import type { CityItem } from '#shared/types/city'

import { cn } from '#shared/lib/cn'
import { REGION_CODES, regionOrigin } from '#shared/lib/region'
import { computed } from 'vue'

import { useRegion } from '~/composables/useRegion'
import { useCities } from '~/composables/useRepoApi'

// Выбор города (T10): обычные HTML-ссылки на эквивалент текущей страницы в другом регионе,
// а если его там нет — на главную региона с пояснением (?nopage=1). Текущий город — текстом.
// Без автоматических переходов по IP или cookie.
withDefaults(defineProps<{ variant?: 'bar' | 'menu' }>(), { variant: 'bar' })

type RegionAlternates = {
  readonly path: string
  readonly exists: Record<RegionCode, boolean>
}

const route = useRoute()
const region = useRegion()
const runtimeConfig = useRuntimeConfig()
const { data: citiesData } = useCities()

// Без await: компонент монтируется и в открывающемся мобильном меню. На сервере данные всё
// равно загружаются до рендера (serverPrefetch), в браузере берутся из payload по ключу.
const { data: alternatesData } = useAsyncData<RegionAlternates>(
  () => `region-alternates:${route.path}:${region.value}`,
  () =>
    $fetch<RegionAlternates>('/api/region_alternates', {
      query: { path: route.path, region: region.value },
    }),
  { watch: [() => route.path] }
)

// Nuxt разбирает JSON из NUXT_PUBLIC_REGION_SWITCH_ORIGINS сам (приходит объект), строку
// тоже принимаем.
const configuredOrigins = computed<Partial<Record<RegionCode, string>>>(() => {
  const raw: unknown = runtimeConfig.public.regionSwitchOrigins
  if (raw && typeof raw === 'object') {
    return raw as Partial<Record<RegionCode, string>>
  }

  const text = typeof raw === 'string' ? raw.trim() : ''
  if (text.length === 0) {
    return {}
  }

  try {
    return JSON.parse(text) as Partial<Record<RegionCode, string>>
  } catch {
    return {}
  }
})

const originOf = (code: RegionCode): string => {
  const configured = configuredOrigins.value[code]
  if (configured) {
    return configured.replace(/\/+$/, '')
  }

  const siteUrl = String(runtimeConfig.public.siteUrl ?? '').trim()
  return siteUrl.length > 0 ? regionOrigin(code, siteUrl) : ''
}

const cityName = (code: RegionCode): string => {
  const cities: readonly CityItem[] = citiesData.value ?? []
  return cities.find((city) => city.slug === code)?.name ?? code
}

const links = computed(() =>
  REGION_CODES.map((code) => {
    const exists = alternatesData.value?.exists[code] ?? false
    return {
      code,
      name: cityName(code),
      current: code === region.value,
      href: exists ? `${originOf(code)}${route.path}` : `${originOf(code)}/?nopage=1`,
    }
  })
)
</script>

<template>
  <nav
    aria-label="Выбор города"
    :class="
      cn(
        'flex flex-wrap items-center gap-x-2 gap-y-1',
        variant === 'menu' ? 'text-base' : 'text-sm'
      )
    ">
    <template v-for="(link, index) in links" :key="link.code">
      <span v-if="index > 0" aria-hidden="true" class="opacity-50">·</span>
      <span v-if="link.current" aria-current="true" class="font-semibold">{{ link.name }}</span>
      <a
        v-else
        :href="link.href"
        class="hover:text-brand-red underline-offset-4 transition-colors hover:underline">
        {{ link.name }}
      </a>
    </template>
  </nav>
</template>
